import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select } from "antd";
import {
  BarHint,
  BarLabel,
  BarNote,
  Chip,
  ChipRow,
  DecidedBanner,
  ErrorBanner,
  PiBar,
  SecondaryButton,
  SignUpButton,
  TextInput,
  ThumbPair,
  ThumbRow,
  TurnDownButton,
} from "../IntakeReviewStyled";
import {
  actOnIntakeReview,
  clearIntakeReviewActionError,
  closeIntakeReviewCard,
  fetchIntakeReviewQueue,
} from "../../../slice/IntakeReviewSlice";
import { secondsOnCard } from "../utils";

/**
 * The thumb bar on the personal-injury card.
 *
 * Built around how the PI reviewer actually works (Matthew, 2026-10-31:
 * "He doesn't like to have to push buttons"): sign-up is one tap with
 * nothing to type; a turn-down is one tap on the reason, as the case
 * system spells it, with a sentence optional; asking intake, having a
 * colleague call the client, and referring out are each one more tap.
 * Everything is at least 44px tall because it is used on a phone.
 */
const PiDecisionBar = () => {
  const dispatch = useDispatch();
  const {
    card,
    acting,
    actionError,
    actionErrorField,
    sectionsOpened,
    openedAt,
    lastDecision,
  } = useSelector((state) => state.intakeReviews);

  // null | "turnDown" | "ask" | "call" | "referOut" | "assign" | "callDone"
  const [armed, setArmed] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [firm, setFirm] = useState("");
  const [assignee, setAssignee] = useState(null);

  const reasons = useMemo(() => card?.turnDownReasons || [], [card]);
  const reviewers = useMemo(() => card?.reviewers || [], [card]);
  const referralFirms = useMemo(() => card?.referralFirms || [], [card]);
  const caller = card?.caller || {};
  const callRequest = card?.callRequest || {};

  if (!card) return null;

  const send = (payload) =>
    dispatch(
      actOnIntakeReview({
        id: card.id,
        sectionsOpened,
        secondsOnCard: secondsOnCard(openedAt),
        ...payload,
      })
    );

  const disarm = () => {
    setArmed(null);
    setReason("");
    setNote("");
    setFirm("");
    setAssignee(null);
    dispatch(clearIntakeReviewActionError());
  };

  const afterQueueMove = () => {
    disarm();
    dispatch(fetchIntakeReviewQueue());
  };

  const callerFirstName = caller.firstName || caller.name || "a colleague";

  if (lastDecision) {
    const words = {
      signed_up: "Signed up. Intake sends the contract.",
      turned_down: "Turned down, reason recorded.",
      referred_out: "Referred out.",
      waiting_on_intake: "Question sent to intake.",
    };
    return (
      <PiBar>
        <DecidedBanner>{words[lastDecision] || "Recorded."}</DecidedBanner>
        <ThumbPair>
          <SignUpButton type="button" onClick={() => dispatch(closeIntakeReviewCard())}>
            Next case
          </SignUpButton>
        </ThumbPair>
      </PiBar>
    );
  }

  if (!card.canDecide) {
    return (
      <PiBar>
        <DecidedBanner>
          {card.statusLabel}
          {card.decidedBy ? ` by ${card.decidedBy.name}` : ""}
          {card.turnDownReason ? ` — ${card.turnDownReason}` : ""}
          {card.decisionNote ? ` — "${card.decisionNote}"` : ""}
        </DecidedBanner>
      </PiBar>
    );
  }

  const error = actionError ? (
    <ErrorBanner role="alert">{actionError}</ErrorBanner>
  ) : null;

  if (armed === "turnDown") {
    return (
      <PiBar>
        {error}
        <BarLabel>Why? Tap one.</BarLabel>
        <ChipRow>
          {reasons.map((r) => (
            <Chip
              key={r.value}
              type="button"
              $active={reason === r.value}
              onClick={() => setReason(r.value)}
              disabled={acting}
              title={r.value}
            >
              {r.label}
            </Chip>
          ))}
        </ChipRow>
        <BarNote
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="A sentence, if there is one worth keeping (optional)."
          $invalid={actionErrorField === "note"}
        />
        <ThumbPair>
          <TurnDownButton
            type="button"
            disabled={acting || !reason}
            onClick={() =>
              send({ action: "turnDown", turnDownReason: reason, note: note.trim() })
            }
          >
            Turn down
          </TurnDownButton>
          <SecondaryButton type="button" onClick={disarm} disabled={acting}>
            Back
          </SecondaryButton>
        </ThumbPair>
      </PiBar>
    );
  }

  if (armed === "ask") {
    return (
      <PiBar>
        {error}
        <BarLabel>What should intake find out?</BarLabel>
        <BarHint>
          Goes to the intake agent as written. The card comes back here with the answer.
        </BarHint>
        <BarNote
          autoFocus
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="e.g. Who owns the property, and is there a business behind it?"
          $invalid={actionErrorField === "note"}
        />
        <ThumbPair>
          <SignUpButton
            type="button"
            disabled={acting || !note.trim()}
            onClick={() => send({ action: "askQuestion", note: note.trim() })}
          >
            Send to intake
          </SignUpButton>
          <SecondaryButton type="button" onClick={disarm} disabled={acting}>
            Back
          </SecondaryButton>
        </ThumbPair>
      </PiBar>
    );
  }

  if (armed === "call") {
    return (
      <PiBar>
        {error}
        <BarLabel>Have {callerFirstName} call the client</BarLabel>
        <BarHint>
          {caller.name || "Your colleague"} gets the card with your note and rings{" "}
          {card.clientName ? card.clientName.split("|")[0].trim() : "the client"}.
          What they learn comes back to you here.
        </BarHint>
        <BarNote
          autoFocus
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="e.g. Does the other driver have insurance? Did he get a ticket?"
        />
        <ThumbPair>
          <SignUpButton
            type="button"
            disabled={acting}
            onClick={() =>
              send({ action: "requestCall", note: note.trim() }).then((result) => {
                if (!result.error) afterQueueMove();
              })
            }
          >
            Send the request
          </SignUpButton>
          <SecondaryButton type="button" onClick={disarm} disabled={acting}>
            Back
          </SecondaryButton>
        </ThumbPair>
      </PiBar>
    );
  }

  if (armed === "callDone") {
    return (
      <PiBar>
        {error}
        <BarLabel>What did the client say?</BarLabel>
        <BarHint>
          Goes back to {callRequest.asked_by_name || "whoever asked"} with the card.
        </BarHint>
        <BarNote
          autoFocus
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="e.g. Other driver has Progressive, 100/300. He was not ticketed."
          $invalid={actionErrorField === "note"}
        />
        <ThumbPair>
          <SignUpButton
            type="button"
            disabled={acting || !note.trim()}
            onClick={() =>
              send({ action: "callDone", note: note.trim() }).then((result) => {
                if (!result.error) afterQueueMove();
              })
            }
          >
            Send it back
          </SignUpButton>
          <SecondaryButton type="button" onClick={disarm} disabled={acting}>
            Back
          </SecondaryButton>
        </ThumbPair>
      </PiBar>
    );
  }

  if (armed === "referOut") {
    return (
      <PiBar>
        {error}
        <BarLabel>Which firm?</BarLabel>
        {referralFirms.length > 0 ? (
          <Select
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Choose a firm"
            value={firm || undefined}
            onChange={(value) => setFirm(value || "")}
            status={actionErrorField === "referredTo" ? "error" : undefined}
            options={referralFirms.map((f) => ({
              value: f.key,
              label:
                f.states && f.states.length
                  ? `${f.name} (${f.states.join(", ")})`
                  : f.name,
            }))}
            showSearch
            optionFilterProp="label"
          />
        ) : (
          <TextInput
            value={firm}
            onChange={(event) => setFirm(event.target.value)}
            $invalid={actionErrorField === "referredTo"}
            placeholder="e.g. Skye Law"
            style={{ marginTop: 8 }}
          />
        )}
        <BarNote
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Why it is going out (required)."
          $invalid={actionErrorField === "note"}
        />
        <ThumbPair>
          <SignUpButton
            type="button"
            disabled={acting || !firm.trim() || !note.trim()}
            onClick={() =>
              send({ action: "referOut", referredTo: firm.trim(), note: note.trim() })
            }
          >
            Refer out
          </SignUpButton>
          <SecondaryButton type="button" onClick={disarm} disabled={acting}>
            Back
          </SecondaryButton>
        </ThumbPair>
      </PiBar>
    );
  }

  if (armed === "assign") {
    return (
      <PiBar>
        {error}
        <BarLabel>Hand it to which reviewer?</BarLabel>
        <Select
          style={{ width: "100%", marginTop: 8 }}
          placeholder="Choose a reviewer"
          value={assignee}
          onChange={setAssignee}
          options={reviewers.map((r) => ({ value: r.id, label: r.name }))}
          notFoundContent="No other reviewers available"
        />
        <ThumbPair>
          <SignUpButton
            type="button"
            disabled={acting || !assignee}
            onClick={() =>
              send({ action: "assign", assigneeId: assignee }).then(() => disarm())
            }
          >
            Hand it over
          </SignUpButton>
          <SecondaryButton type="button" onClick={disarm} disabled={acting}>
            Back
          </SecondaryButton>
        </ThumbPair>
      </PiBar>
    );
  }

  // The resting bar: one tap to sign up, one tap to start a turn-down.
  const callOpen = callRequest.to && !callRequest.answer;
  return (
    <PiBar>
      {error}
      <ThumbPair>
        <SignUpButton
          type="button"
          onClick={() => send({ action: "signUp", note: "" })}
          disabled={acting}
        >
          Sign up
        </SignUpButton>
        <TurnDownButton
          type="button"
          onClick={() => setArmed("turnDown")}
          disabled={acting}
        >
          Turn down
        </TurnDownButton>
      </ThumbPair>
      <ThumbRow>
        <SecondaryButton type="button" onClick={() => setArmed("ask")} disabled={acting}>
          Ask intake
        </SecondaryButton>
        {callOpen ? (
          <SecondaryButton type="button" onClick={() => setArmed("callDone")} disabled={acting}>
            Called — report
          </SecondaryButton>
        ) : (
          <SecondaryButton
            type="button"
            onClick={() => setArmed("call")}
            disabled={acting || !caller.email}
            title={caller.email ? "" : "No one is set up to take call requests"}
          >
            Have {callerFirstName} call
          </SecondaryButton>
        )}
        <SecondaryButton type="button" onClick={() => setArmed("referOut")} disabled={acting}>
          Refer out
        </SecondaryButton>
      </ThumbRow>
      {reviewers.length > 0 ? (
        <ThumbRow style={{ gridTemplateColumns: "1fr" }}>
          <SecondaryButton type="button" onClick={() => setArmed("assign")} disabled={acting}>
            Hand over
          </SecondaryButton>
        </ThumbRow>
      ) : null}
    </PiBar>
  );
};

export default PiDecisionBar;
